<template>
  <div class="fs-scrollable">
    <transition name="simple-fade-in-out">
      <div
        v-if="showShadows !== 'bottom' && isScrollable && isTopShadowVisible"
        :class="{
          'fs-scrollable__shadow': true,
          'fs-scrollable__shadow--top': true,
          'fs-scrollable__shadow--color-white': shadowsColor === 'white',
          'fs-scrollable__shadow--color-light-gray': shadowsColor === 'light-gray',
          'fs-scrollable__shadow--color-dark-gray': shadowsColor === 'dark-gray',
        }"
      >
        &nbsp;
      </div>
    </transition>

    <transition name="simple-fade-in-out">
      <div
        v-if="showShadows !== 'top' && isScrollable && isBottomShadowVisible"
        :class="{
          'fs-scrollable__shadow': true,
          'fs-scrollable__shadow--bottom': true,
          'fs-scrollable__shadow--color-white': shadowsColor === 'white',
          'fs-scrollable__shadow--color-light-gray': shadowsColor === 'light-gray',
          'fs-scrollable__shadow--color-dark-gray': shadowsColor === 'dark-gray',
        }"
      >
        &nbsp;
      </div>
    </transition>

    <div
      ref="scrollableContainer"
      :class="{
        'fs-scrollable__inner': true,
        'fs-scrollable__inner--scrollbar': isScrollable,
      }"
      @scroll="toggleShadows($event)"
    >
      <div
        ref="contentWrapper"
        :class="{
          'fs-scrollable__content-wrapper': true,
          'fs-scrollable__content-wrapper--scrollable': isScrollable,
          'fs-scrollable__content-wrapper--constant-width': isConstantWidth,
        }"
      >
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  emits: ['is-scrollable'],
  props: {
    shadowsColor: {
      type: String,
      validator: (color) => ['white', 'light-gray', 'dark-gray'].includes(color),
      default: 'white',
    },
    showShadows: {
      type: String,
      validator: (color) => ['top', 'bottom', 'both'].includes(color),
      default: 'both',
    },
    isConstantWidth: {
      // Always make room for the scrollbar, so content is not pushed to the left when the scrollbar appears.
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isScrollable: false,

      isTopShadowVisible: false,
      isBottomShadowVisible: true,
    };
  },

  mounted() {
    this.setIsScrollable();
  },

  updated() {
    this.setIsScrollable();
  },

  methods: {
    getScrollTop() {
      return this.$refs.scrollableContainer.scrollTop;
    },

    setScrollTop(scrollTop) {
      this.$refs.scrollableContainer.scrollTop = scrollTop;
    },

    toggleShadows(event) {
      if (!this.isScrollable) {
        this.isTopShadowVisible = false;
        this.isBottomShadowVisible = false;

        return;
      }

      const shadowShowThreshold = 5;
      const minScrollPosition = shadowShowThreshold;
      const maxScrollPosition = event.target.scrollHeight - event.target?.clientHeight - shadowShowThreshold;
      const currentScrollPosition = event.target.scrollTop;

      this.isTopShadowVisible = currentScrollPosition > minScrollPosition;
      this.isBottomShadowVisible = currentScrollPosition < maxScrollPosition;
    },

    setIsScrollable() {
      const scrollableContainerHeight = this.$refs.scrollableContainer?.clientHeight;
      const contentContainerHeight = this.$refs.contentWrapper?.clientHeight;

      this.isScrollable = scrollableContainerHeight < contentContainerHeight;
    },
  },

  watch: {
    isScrollable() {
      this.$emit('is-scrollable', this.isScrollable);
    },
  },
};
</script>

<style lang="scss">
@import '@styles/variables';

// Fs Scrollable ------------------------------------------------------------
.fs-scrollable {
  position: relative;
  display: flex;
  overflow: hidden;

  &__shadow {
    width: calc(100% - 1.6rem);
    height: 6rem;
    max-height: 10%;
    position: absolute;
    left: 0;
    z-index: 2;
    background: rgba(255, 0, 0, 0.1);
    pointer-events: none;

    &--top {
      top: 0;
      background: rgb(255, 255, 255);
      background: linear-gradient(180deg, rgba(255, 255, 255, 1) 0, rgba(255, 255, 255, 0) 100%);

      &.fs-scrollable__shadow--color-light-gray {
        background: $c-bg-gray-darker-hover;
        background: linear-gradient(180deg, rgba($c-bg-gray-darker-hover, 1) 0, rgba($c-bg-gray-darker-hover, 0) 100%);
      }

      &.fs-scrollable__shadow--color-dark-gray {
        background: $c-bg-gray-darker-hover;
        background: linear-gradient(180deg, rgba($c-bg-gray-darkest, 1) 0, rgba($c-bg-gray-darkest, 0) 100%);
      }

      &.fs-scrollable__shadow--color-white {
        background: rgb(255, 255, 255);
        background: linear-gradient(180deg, rgba(255, 255, 255, 1) 0, rgba(255, 255, 255, 0) 100%);
      }
    }

    &--bottom {
      bottom: 0;
      background: rgb(255, 255, 255);
      background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0, rgba(255, 255, 255, 0) 100%);

      &.fs-scrollable__shadow--color-light-gray {
        background: $c-bg-gray-darker-hover;
        background: linear-gradient(0deg, rgba($c-bg-gray-darker-hover, 1) 0, rgba($c-bg-gray-darker-hover, 0) 100%);
      }

      &.fs-scrollable__shadow--color-dark-gray {
        background: $c-bg-gray-darker-hover;
        background: linear-gradient(0deg, rgba($c-bg-gray-darkest, 1) 0, rgba($c-bg-gray-darkest, 0) 100%);
      }

      &.fs-scrollable__shadow--color-white {
        background: rgb(255, 255, 255);
        background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0, rgba(255, 255, 255, 0) 100%);
      }
    }
  }

  &__inner {
    width: 100%;
    max-height: 100%;
    padding: 0;
    position: relative;
    overflow-x: hidden;
    scrollbar-width: thin;
    z-index: 1;

    &--scrollable {
      overflow-y: scroll;
    }

    &::-webkit-scrollbar {
      width: 0.8rem;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0);
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 10rem;
      background: rgba($c-dark, 0.15);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba($c-dark, 0.25);
    }
  }

  &__content-wrapper {
    &--scrollable {
      padding-right: 0.8rem;
    }

    &--constant-width {
      padding-right: 0.8rem;
    }
  }
}
</style>
